import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store';
import { useAppDispatch, useAppSelector } from './hooks/redux';
import { verifyToken } from './store/slices/authSlice';
import { apiClient } from './utils/apiClient';
import LoginForm from './components/auth/LoginForm';
import ProtectedRoute from './components/layout/ProtectedRoute';
import AppLayout from './components/layout/AppLayout';
import Dashboard from './pages/Dashboard';
import UserManagement from './pages/UserManagement';
import OrderManagement from './pages/OrderManagement';
import InventoryManagement from './pages/InventoryManagement';
import StockEntry from './pages/StockEntry';
import DirectStockApproval from './pages/DirectStockApproval';
import StyleManagement from './pages/StyleManagement';
import OrderPicking from './pages/OrderPicking';
import OrderVerification from './pages/OrderVerification';
import Reports from './pages/Reports';

const AppContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, loading } = useAppSelector((state) => state.auth);  useEffect(() => {
    // Initialize API client with stored token and verify if needed
    const token = localStorage.getItem('token');
    
    if (token) {
      // Always set the token in API client on app load
      apiClient.setAuthToken(token);
      
      // Verify the token to ensure it's still valid
      // This will update the authentication state if the token is invalid
      dispatch(verifyToken());
    }
  }, [dispatch]);
  // Show loading only if we're verifying token and not authenticated yet
  if (loading && !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        <Route 
          path="/login" 
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginForm />
          } 
        />
        
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Routes>
                  <Route path="dashboard" element={<Dashboard />} />
                  
                  {/* Manager routes */}                  <Route 
                    path="styles" 
                    element={
                      <ProtectedRoute allowedRoles={['manager']}>
                        <StyleManagement />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="users" 
                    element={
                      <ProtectedRoute allowedRoles={['manager']}>
                        <UserManagement />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="settings" 
                    element={
                      <ProtectedRoute allowedRoles={['manager']}>
                        <div className="p-8 text-center">
                          <h1 className="text-2xl font-bold">Settings</h1>
                          <p className="text-gray-600 mt-2">Manager only - Settings coming soon</p>
                        </div>
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Executive1 routes */}
                  <Route 
                    path="orders" 
                    element={
                      <ProtectedRoute allowedRoles={['manager', 'executive1']}>
                        <OrderManagement />
                      </ProtectedRoute>
                    } 
                  />
                    {/* Executive2 routes */}                  <Route 
                    path="stock-entry" 
                    element={
                      <ProtectedRoute allowedRoles={['manager', 'executive2']}>
                        <StockEntry />
                      </ProtectedRoute>
                    } 
                  />                  <Route 
                    path="stock-approval" 
                    element={
                      <ProtectedRoute allowedRoles={['manager', 'executive2']}>
                        <DirectStockApproval />
                      </ProtectedRoute>
                    } 
                  />                  <Route 
                    path="order-verification" 
                    element={
                      <ProtectedRoute allowedRoles={['manager', 'executive2']}>
                        <OrderVerification />
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Worker routes */}                  <Route 
                    path="picking" 
                    element={
                      <ProtectedRoute allowedRoles={['manager', 'worker']}>
                        <OrderPicking />
                      </ProtectedRoute>
                    } 
                  />
                    {/* Shared routes */}
                  <Route 
                    path="inventory" 
                    element={
                      <InventoryManagement />
                    } 
                  />                  <Route 
                    path="reports" 
                    element={
                      <ProtectedRoute allowedRoles={['manager']}>
                        <Reports />
                      </ProtectedRoute>
                    } 
                  />
                  
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </AppLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
};

export default App;
